import { useController, useFormContext } from 'react-hook-form'
import { z } from 'zod'

import ArrowIcon from '@/assets/icons/icon-chevron-down.svg'
import { TextField } from '@/components/ui/text-field.tsx'
import { Tooltip } from '@/components/ui/tooltip'
import { FAN_NUMBERING_MAX, FAN_NUMBERING_MIN } from '@/consts/fan-numbering'
import { FanNumberingFormSchema } from '@/modules/automations/ui/forms/fan-numbering-form.tsx'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption'

type FanNumberingFormSchemaType = z.infer<typeof FanNumberingFormSchema>

interface FanNumberingSettingsProps {
  isLoading?: boolean
  hasFullPermission: boolean
  onChange: (maxNumber: number) => void
}

export const FanNumberingSettings = ({
  isLoading,
  hasFullPermission,
  onChange,
}: FanNumberingSettingsProps) => {
  const { control } = useFormContext<FanNumberingFormSchemaType>()
  const { field, fieldState } = useController({ control, name: 'maxNumbers' })

  const arrowClassName =
    isLoading || !hasFullPermission
      ? 'cursor-not-allowed opacity-30'
      : 'cursor-pointer hover:opacity-50'

  const handleChange = (value: number) => {
    field.onChange(value)
    onChange(value)
  }

  const increment = () => {
    const newValue = Math.min(field.value + 1, FAN_NUMBERING_MAX)

    handleChange(newValue)
  }

  const decrement = () => {
    const newValue = Math.max(field.value - 1, FAN_NUMBERING_MIN)

    handleChange(newValue)
  }

  return (
    <>
      <hr />
      <SettingsOption
        title="Set Numbers"
        description="Set the amount of numbers that you want to have the chats assigned with (eg. 1, 2, 3 if you set the maximum number to 3)."
        biggerTitle
        mobileVertical
        disableTopPadding
        disableBottomPadding
      >
        <div className="flex flex-col">
          <Tooltip content="Changing this setting disables Fan Numbering while the changes are being applied. This usually takes a few minutes">
            <TextField
              max={FAN_NUMBERING_MAX}
              min={FAN_NUMBERING_MIN}
              type="number"
              showPrefixSeparator
              className={fieldState.error ? 'border-red-500' : ''}
              prefix={
                <span className="text-secondary-300 truncate">Max Number</span>
              }
              disabled={isLoading || !hasFullPermission}
              suffix={
                <div>
                  <ArrowIcon
                    type="button"
                    className={`mb-[-5px] rotate-180 dark:text-white ${arrowClassName}`}
                    onClick={increment}
                  />

                  <ArrowIcon
                    type="button"
                    className={`dark:text-white ${arrowClassName}`}
                    onClick={decrement}
                  />
                </div>
              }
              value={field.value}
              onChange={(event) => {
                if (isLoading) return

                const value = event.target.value

                if (value === '') {
                  handleChange(0)

                  return
                }

                const maxNumber = parseInt(value, 10)

                if (!isNaN(maxNumber)) {
                  handleChange(maxNumber)
                }
              }}
            />
          </Tooltip>
          {fieldState.error && (
            <span className="mt-1 text-sm text-red-500">
              {fieldState.error.message}
            </span>
          )}
        </div>
      </SettingsOption>
    </>
  )
}
